import { FC, useMemo, useCallback, useEffect } from 'react';
import { useHookstate } from '@hookstate/core';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { NextGamePanelMobile } from '../../components/NextGamePanel/mobile';
import { NextGamePanelDesktop } from '../../components/NextGamePanel/desktop';
import { Part } from '../../components/GamePart';
import { useGameType } from '../../hooks/use-game-type';
import { useJackpot } from '../../hooks/use-jackpot';
import { useGamePart } from '../../hooks/use-game-part';
import { useCountdown } from '../../hooks/use-countdown';
import { useSoundHandler } from '../../hooks/use-sound-handler';
import { useCycleGameParts } from '../../hooks/use-cycle-game-parts';
import { useFormatCurrency } from '../../hooks/use-format-currency';
import { meta, next, autoBuy, screenConfig } from '../../stores/backend/';
import { chatMeta } from '../../stores/chat';
import { formatNumber } from '../../lib/format-number';
import { useTicketPrice } from '../../hooks/use-ticket-price';

export const NextGamePanel: FC = () => {
    const { t } = useTranslation();

    const $meta = useHookstate(meta);
    const $next = useHookstate(next);
    const $autoBuy = useHookstate(autoBuy);
    const $chatMeta = useHookstate(chatMeta);
    const {
        screenConfig: { display },
    } = useHookstate(screenConfig);

    const gameType = useGameType($next);
    const jackpot = useJackpot($next, $meta);
    const ticketPrice = useTicketPrice($next);
    const { allGameParts } = useGamePart($next, $meta);
    const { seconds } = useCountdown(new Date($meta.timer.date.value));
    const formatCurrency = useFormatCurrency($next, $meta);

    const to = new Date($meta.timer.date.value);
    const gameCurrency = $next.game_currency.value;
    const $ticketPrice = ticketPrice
        ? formatCurrency(ticketPrice, gameCurrency)
        : t('buyBtn.buy.free');

    const parts = useMemo(
        () =>
            Object.values($next.game_parts.value || {}).map((part) => ({
                patterns: part.pat_code || [],
                matches: part.num_matches || 1,
            })),
        [$next.game_parts.value]
    );

    const size = useMemo(
        () => Object.keys(allGameParts).length,
        [allGameParts]
    );

    const index = useCycleGameParts(size);
    const prizeString = allGameParts[index]?.prizeString;

    const patternName = allGameParts[index]?.desc;

    const gamePrize =
        prizeString && prizeString.length > 1
            ? prizeString[0]
            : allGameParts[index]?.prize;
    const isSalesClosedCountdown = seconds > 0 && seconds <= 20;
    const isNextGameMode = $next.game_mode.value === 'GAME_MODE';
    const shouldShowNextFireBallCue = isSalesClosedCountdown || isNextGameMode;

    const onInfoBox = useCallback(() => {
        $meta.modals.gameInfoFireBallEnabled.set(false);
        $meta.modals.gameInfo.set(true);
    }, [$meta.modals.gameInfo, $meta.modals.gameInfoFireBallEnabled]);

    const onBuy = () => {
        $meta.modals.buy.set(true);
    };

    const $onBuy = useSoundHandler(onBuy, 'click');

    const Component = isMobile ? NextGamePanelMobile : NextGamePanelDesktop;

    const isPatternNameUndefined =
        typeof patternName === 'undefined' || patternName === 'undefined';

    useEffect(() => {
        if (isPatternNameUndefined && $next.game_sid.value !== 0) {
            Sentry.captureMessage('Undefined next game pattern name', {
                level: 'info',
                extra: {
                    nextParts: Object.entries(
                        $next.game_parts.get({ noproxy: true }) || {}
                    ).map(([key, value]) => {
                        return {
                            index: key,
                            name: value.name,
                        };
                    }),
                    partIndex: index,
                    patternName,
                },
            });
        }
    }, [$next, patternName, index, isPatternNameUndefined]);

    return (
        <Component
            {...jackpot}
            to={to}
            parts={parts}
            seconds={seconds}
            gameType={gameType}
            gameParts={size as Part}
            fireBall={
                shouldShowNextFireBallCue ? $next.fireball?.value : undefined
            }
            gameTitle={$next.game_desc.value}
            currentPart={(index + 1) as Part}
            patternType={gameType}
            patternName={patternName}
            gameLinked={$next.game_linked?.value}
            gameContinued={$next.game_continued?.value}
            chatOpen={$chatMeta.chatToggle.value}
            autoBuy={$autoBuy.activation.value}
            onBuy={$onBuy}
            onInfoBox={onInfoBox}
            gameCards={formatNumber($next.game_cards.value || 0)}
            gamePlayers={formatNumber($next.game_players.value || 0)}
            gamePrize={formatCurrency(gamePrize, gameCurrency)}
            ticketPrice={$ticketPrice}
            showTotalTickets={display.showTotalTickets.value}
            isGameLive={false} // TODO: Implement isGameLive value
            isPatternNameRendered={!isPatternNameUndefined}
        />
    );
};
